import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/lib/api";
import { ProgressBar } from "@/lib/phases";
import { BookOpen, CheckCircle, Circle, Printer, Warning } from "@phosphor-icons/react";

const PRIORITY = {
  high: { label: "High", color: "#B23B1F", bg: "#FDECE8" },
  medium: { label: "Medium", color: "#8A6A3B", bg: "#F6EEDF" },
  low: { label: "Low", color: "#4A6B53", bg: "#E8F0EA" },
};

export default function PublicReport() {
  const { shareId } = useParams();
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API}/public/reports/${shareId}`)
      .then((r) => {
        if (!r.ok) throw new Error("not found");
        return r.json();
      })
      .then(setReport)
      .catch(() => setError("This report link is invalid or has been revoked."));
  }, [shareId]);

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--bk-page)" }}>
        <div className="bk-card p-10 text-center max-w-md">
          <h1 className="font-display text-2xl font-bold mb-2" style={{ color: "var(--bk-text)" }}>Report unavailable</h1>
          <p style={{ color: "var(--bk-text-2)" }}>{error}</p>
        </div>
      </div>
    );

  if (!report)
    return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bk-page)" }}><span className="font-display text-2xl" style={{ color: "var(--bk-accent)" }}>Loading report…</span></div>;

  const pct = report.overall.total ? Math.round((report.overall.done / report.overall.total) * 100) : 0;

  return (
    <div className="min-h-screen" style={{ background: "var(--bk-page)" }}>
      <header className="border-b print:hidden" style={{ borderColor: "var(--bk-border)", background: "var(--bk-surface)" }}>
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "var(--bk-accent)" }}>
              <BookOpen size={20} weight="duotone" color="#fff" />
            </span>
            <span className="font-display text-xl font-bold">IronRoot</span>
          </div>
          <button onClick={() => window.print()} data-testid="print-report" className="bk-btn-primary px-5 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-2">
            <Printer size={18} weight="duotone" /> Print / Save PDF
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="public-report">
        <span className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "var(--bk-accent)" }}>Bookkeeping Progress Report</span>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2" style={{ color: "var(--bk-text)" }}>{report.name}</h1>
        <p className="text-base mt-1" style={{ color: "var(--bk-text-2)" }}>
          {[report.entity_type, report.industry].filter(Boolean).join(" · ")}
        </p>

        <div className="bk-card p-6 mt-6">
          <div className="flex items-end justify-between mb-2">
            <span className="font-display text-2xl font-bold" style={{ color: "var(--bk-text)" }}>{pct}% complete</span>
            <span className="text-sm" style={{ color: "var(--bk-text-2)" }}>{report.overall.done} of {report.overall.total} steps</span>
          </div>
          <ProgressBar done={report.overall.done} total={report.overall.total} />
        </div>

        {report.phases.map((phase) => (
          <section key={phase.id} className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl font-bold" style={{ color: "var(--bk-text)" }}>{phase.title}</h2>
              <span className="text-sm font-semibold" style={{ color: "var(--bk-success)" }}>{phase.done}/{phase.total}</span>
            </div>
            {phase.sections.map((section) => (
              <div key={section.title} className="mb-5">
                <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: "var(--bk-accent)" }}>{section.title}</p>
                <div className="space-y-2">
                  {section.steps.map((step, i) => (
                    <div key={i} className="bk-card p-3.5">
                      <div className="flex items-start gap-3">
                        {step.done ? (
                          <CheckCircle size={22} weight="fill" color="var(--bk-success)" className="shrink-0" />
                        ) : (
                          <Circle size={22} weight="regular" color="var(--bk-disabled)" className="shrink-0" />
                        )}
                        <span className="text-sm" style={{ color: step.done ? "var(--bk-disabled)" : "var(--bk-text)", textDecoration: step.done ? "line-through" : "none" }}>
                          {step.title}
                        </span>
                      </div>
                      {(step.note || (step.tasks && step.tasks.length > 0)) && (
                        <div className="mt-2.5 ml-9 space-y-1.5">
                          {step.note && (
                            <p className="text-xs italic" style={{ color: "var(--bk-text-2)" }}>“{step.note}”</p>
                          )}
                          {step.tasks && step.tasks.map((t, ti) => (
                            <div key={ti} className="flex items-center gap-2 text-xs" style={{ color: t.done ? "var(--bk-disabled)" : "var(--bk-text)" }}>
                              {t.done ? <CheckCircle size={14} weight="fill" color="var(--bk-success)" /> : <Circle size={14} color="var(--bk-disabled)" />}
                              <span style={{ textDecoration: t.done ? "line-through" : "none" }}>{t.text}</span>
                              {t.assignee && <span style={{ color: "var(--bk-text-2)" }}>· {t.assignee}</span>}
                              {t.due_date && <span style={{ color: "var(--bk-text-2)" }}>· due {t.due_date}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </section>
        ))}

        {report.diagnosis && report.diagnosis.issues?.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display text-2xl font-bold mb-2" style={{ color: "var(--bk-text)" }}>AI Cleanup Diagnosis</h2>
            <p className="text-sm mb-4 leading-relaxed" style={{ color: "var(--bk-text-2)" }}>{report.diagnosis.summary}</p>
            <div className="space-y-3">
              {report.diagnosis.issues.map((it, i) => {
                const p = PRIORITY[it.priority] || PRIORITY.medium;
                return (
                  <div key={i} className="bk-card p-4">
                    <div className="flex items-start justify-between gap-3">
                      <h4 className="font-semibold text-sm flex items-center gap-2" style={{ color: "var(--bk-text)" }}>
                        <Warning size={16} weight="fill" color={p.color} /> {it.title}
                      </h4>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full shrink-0" style={{ background: p.bg, color: p.color }}>{p.label}</span>
                    </div>
                    <p className="text-sm mt-1.5" style={{ color: "var(--bk-text)" }}><span className="font-semibold" style={{ color: "var(--bk-success)" }}>Fix: </span>{it.action}</p>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <p className="text-xs mt-10 text-center" style={{ color: "var(--bk-disabled)" }}>
          Generated by IronRoot · {new Date(report.generated_at).toLocaleString()}
        </p>
      </main>
    </div>
  );
}
